/**
 * "Updated 4m ago" plus a Refresh button for a dashboard. The label follows
 * the payload's `as_of` (so data restored from the browser cache shows its
 * real age) and re-renders every 30 seconds; the icon spins while anything
 * is fetching and the button is disabled during a forced refresh.
 */
import { RefreshCw } from "lucide-react";
import { useEffect, useState, type FC } from "react";

import { HeaderIconButton } from "@/components/app-header";
import { MetaLabel } from "@/components/surface";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";
import { relativeTime } from "@/features/agents/lib/time";
import { cn } from "@/lib/utils";

export const LAST_UPDATED_TICK_MS = 30_000;

export const lastUpdatedLabel = (
  updatedAt: string | null | undefined,
  now: number = Date.now()
): string | null => {
  const rel = relativeTime(updatedAt, now);
  if (!rel) return null;
  return rel === "now" ? "Updated just now" : `Updated ${rel} ago`;
};

const exactTime = (iso: string): string => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleString(undefined, {
        weekday: "short",
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit"
      });
};

/** Re-render on a timer so a relative label keeps up with the clock. */
const useNow = (intervalMs: number): number => {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), intervalMs);
    return () => window.clearInterval(id);
  }, [intervalMs]);
  return now;
};

type Props = {
  /** `as_of` of the data on screen. */
  updatedAt: string | null | undefined;
  /** Any dashboard query is fetching (spins the icon). */
  isFetching?: boolean;
  /** A forced refresh is running (disables the button). */
  isRefreshing?: boolean;
  onRefresh: () => void;
  className?: string;
};

export const LastUpdated: FC<Props> = ({
  updatedAt,
  isFetching = false,
  isRefreshing = false,
  onRefresh,
  className
}) => {
  const now = useNow(LAST_UPDATED_TICK_MS);
  const label = lastUpdatedLabel(updatedAt, now);
  return (
    <div
      className={cn("flex shrink-0 items-center gap-1", className)}
      data-testid="last-updated"
    >
      {label && updatedAt ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <MetaLabel
              className="hidden normal-case tracking-normal sm:inline @max-lg:hidden"
              data-testid="last-updated-label"
            >
              {label}
            </MetaLabel>
          </TooltipTrigger>
          <TooltipContent side="bottom">{exactTime(updatedAt)}</TooltipContent>
        </Tooltip>
      ) : null}
      <HeaderIconButton
        label="Refresh"
        onClick={onRefresh}
        disabled={isRefreshing}
        data-testid="last-updated-refresh"
      >
        <RefreshCw
          className={cn(
            "size-4",
            (isFetching || isRefreshing) && "animate-spin"
          )}
          data-spinning={isFetching || isRefreshing ? "true" : undefined}
        />
      </HeaderIconButton>
    </div>
  );
};
